/**
 * Poseidon dashboard — reconnecting WebSocket hook.
 *
 * The socket is push-only: it carries `DashboardEvent` frames from the daemon to
 * the UI. Operator commands never travel over it; they go through the REST
 * command endpoints so that each one carries a CSRF header, an idempotency key
 * and a typed confirmation phrase.
 *
 * Reconnect policy: exponential backoff with jitter, capped, plus a heartbeat
 * watchdog. A missed `seq` is treated as data loss: the hook reports a gap so
 * pages refetch their REST snapshots rather than silently rendering a stream
 * with a hole in it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DASHBOARD_EVENT_TYPES } from './types';
import type { DashboardEvent } from './types';
import { SOCKET_POLICY, resolveWebSocketUrl } from '../config';

export type SocketStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed' | 'error';

export interface DashboardSocketState {
  status: SocketStatus;
  /** Reconnect attempts since the last successful open. */
  attempts: number;
  lastEventAt: string | null;
  lastSeq: number | null;
  /** Frames the daemon emitted that this client never received (seq gaps). */
  missedEvents: number;
  /** Increments every time the UI should discard deltas and refetch snapshots. */
  resyncToken: number;
  /** Event types this bundle does not understand — a version-skew signal. */
  unknownTypes: string[];
  lastError: string | null;
}

export interface UseDashboardSocketOptions {
  /** Defaults to the same-origin `/ws` (proxied to 127.0.0.1:8000 in dev). */
  url?: string;
  enabled?: boolean;
  onEvent: (event: DashboardEvent) => void;
}

const KNOWN_EVENT_TYPES: ReadonlySet<string> = new Set<string>(DASHBOARD_EVENT_TYPES);

type ParseResult =
  | { ok: true; event: DashboardEvent }
  | { ok: false; reason: 'malformed' | 'unknown_type' | 'unsupported_version'; detail: string };

/**
 * Validate a frame before it is allowed to reach page state. Anything that does
 * not match the envelope contract is dropped loudly rather than coerced.
 */
export function parseDashboardEvent(raw: unknown): ParseResult {
  if (typeof raw !== 'object' || raw === null) {
    return { ok: false, reason: 'malformed', detail: 'frame was not a JSON object' };
  }
  const frame = raw as Record<string, unknown>;
  if (frame['v'] !== 1) {
    return { ok: false, reason: 'unsupported_version', detail: `envelope version ${String(frame['v'])} is not supported` };
  }
  const type = frame['type'];
  if (typeof type !== 'string') {
    return { ok: false, reason: 'malformed', detail: 'missing event type' };
  }
  if (!KNOWN_EVENT_TYPES.has(type)) {
    return { ok: false, reason: 'unknown_type', detail: `unknown event type "${type}"` };
  }
  if (typeof frame['seq'] !== 'number' || typeof frame['emitted_at'] !== 'string') {
    return { ok: false, reason: 'malformed', detail: 'missing seq or emitted_at' };
  }
  if (typeof frame['payload'] !== 'object' || frame['payload'] === null) {
    return { ok: false, reason: 'malformed', detail: 'missing payload' };
  }
  return { ok: true, event: frame as unknown as DashboardEvent };
}

export function useDashboardSocket(
  options: UseDashboardSocketOptions,
): DashboardSocketState & { reconnect: () => void } {
  const { url, enabled = true, onEvent } = options;

  const [state, setState] = useState<DashboardSocketState>({
    status: 'idle',
    attempts: 0,
    lastEventAt: null,
    lastSeq: null,
    missedEvents: 0,
    resyncToken: 0,
    unknownTypes: [],
    lastError: null,
  });

  // Held in refs so a new handler identity never forces a reconnect.
  const handlerRef = useRef(onEvent);
  const socketRef = useRef<WebSocket | null>(null);
  const attemptsRef = useRef(0);
  const lastSeqRef = useRef<number | null>(null);
  const lastFrameAtRef = useRef<number>(0);
  const retryTimerRef = useRef<number | null>(null);
  const disposedRef = useRef(false);
  const [manualNonce, setManualNonce] = useState(0);

  useEffect(() => {
    handlerRef.current = onEvent;
  }, [onEvent]);

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState((prev) => ({ ...prev, status: 'idle' }));
      return;
    }

    disposedRef.current = false;
    const target = url ?? resolveWebSocketUrl();

    const connect = (): void => {
      if (disposedRef.current) return;
      const isRetry = attemptsRef.current > 0;
      setState((prev) => ({ ...prev, status: isRetry ? 'reconnecting' : 'connecting' }));

      let socket: WebSocket;
      try {
        socket = new WebSocket(target);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'could not open the socket';
        setState((prev) => ({ ...prev, status: 'error', lastError: message }));
        scheduleRetry();
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        if (disposedRef.current) {
          socket.close();
          return;
        }
        attemptsRef.current = 0;
        lastFrameAtRef.current = Date.now();
        setState((prev) => ({ ...prev, status: 'open', attempts: 0, lastError: null }));
      };

      socket.onmessage = (message: MessageEvent<string>) => {
        lastFrameAtRef.current = Date.now();
        let parsed: unknown;
        try {
          parsed = JSON.parse(message.data);
        } catch {
          setState((prev) => ({ ...prev, lastError: 'dropped a malformed frame from the daemon' }));
          return;
        }
        const result = parseDashboardEvent(parsed);
        if (!result.ok) {
          if (result.reason === 'unknown_type') {
            setState((prev) => ({
              ...prev,
              unknownTypes: prev.unknownTypes.includes(result.detail)
                ? prev.unknownTypes
                : [...prev.unknownTypes, result.detail].slice(-SOCKET_POLICY.maxUnknownTypeReports),
            }));
          } else {
            setState((prev) => ({ ...prev, lastError: result.detail }));
          }
          return;
        }

        const event = result.event;
        const previous = lastSeqRef.current;
        const isGap = previous !== null && event.seq > previous + 1;
        const isOutOfOrder = previous !== null && event.seq <= previous;
        lastSeqRef.current = Math.max(previous ?? 0, event.seq);

        setState((prev) => ({
          ...prev,
          lastEventAt: event.emitted_at,
          lastSeq: lastSeqRef.current,
          missedEvents: isGap ? prev.missedEvents + (event.seq - (previous ?? 0) - 1) : prev.missedEvents,
          // A gap or a replayed frame both mean local state may be wrong: ask
          // pages to refetch their snapshots instead of trusting the stream.
          resyncToken: isGap || isOutOfOrder ? prev.resyncToken + 1 : prev.resyncToken,
        }));

        if (!isOutOfOrder) {
          handlerRef.current(event);
        }
      };

      socket.onerror = () => {
        // Browsers deliberately withhold detail here; the close handler carries
        // the actionable signal.
        setState((prev) => ({ ...prev, lastError: 'websocket error' }));
      };

      socket.onclose = (event: CloseEvent) => {
        socketRef.current = null;
        if (disposedRef.current) {
          setState((prev) => ({ ...prev, status: 'closed' }));
          return;
        }
        setState((prev) => ({
          ...prev,
          status: 'reconnecting',
          lastError: event.reason.length > 0 ? event.reason : `connection closed (code ${event.code})`,
        }));
        scheduleRetry();
      };
    };

    const scheduleRetry = (): void => {
      if (disposedRef.current) return;
      clearRetry();
      const attempt = attemptsRef.current;
      attemptsRef.current = attempt + 1;
      const exponential = Math.min(SOCKET_POLICY.maxBackoffMs, SOCKET_POLICY.baseBackoffMs * 2 ** attempt);
      // Full jitter: avoids a reconnect storm when several tabs are open.
      const delay = Math.round(exponential * (0.5 + Math.random() * 0.5));
      setState((prev) => ({ ...prev, attempts: attempt + 1 }));
      retryTimerRef.current = window.setTimeout(connect, delay);
    };

    connect();

    // Heartbeat watchdog: a silent socket is worse than a closed one, because a
    // stale ribbon looks identical to a quiet market.
    const watchdog = window.setInterval(() => {
      const socket = socketRef.current;
      if (socket === null || socket.readyState !== WebSocket.OPEN) return;
      if (Date.now() - lastFrameAtRef.current > SOCKET_POLICY.heartbeatTimeoutMs) {
        setState((prev) => ({ ...prev, lastError: 'no daemon heartbeat within the freshness window; reconnecting' }));
        // Closing triggers onclose, which schedules the retry.
        socket.close(4000, 'heartbeat timeout');
      }
    }, Math.max(2_000, Math.floor(SOCKET_POLICY.heartbeatTimeoutMs / 2)));

    // Reconnect promptly when the tab becomes visible again.
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      const socket = socketRef.current;
      if (socket === null || socket.readyState === WebSocket.CLOSED || socket.readyState === WebSocket.CLOSING) {
        clearRetry();
        attemptsRef.current = 0;
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposedRef.current = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(watchdog);
      clearRetry();
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket !== null) {
        socket.onclose = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onopen = null;
        socket.close(1000, 'dashboard unmounted');
      }
    };
  }, [url, enabled, clearRetry, manualNonce]);

  const reconnect = useCallback(() => {
    attemptsRef.current = 0;
    clearRetry();
    socketRef.current?.close(1000, 'manual reconnect');
    setManualNonce((n) => n + 1);
  }, [clearRetry]);

  // Memoised so a consumer's context value does not change identity on every
  // render just because this hook returned a fresh object literal.
  return useMemo(() => ({ ...state, reconnect }), [state, reconnect]);
}
